import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import {
  campaignValidationSelector,
  emailTemplateFormFieldsSelector,
  feedbackPageFormFieldsSelector,
  landingPageFormFieldsSelector,
} from "../selectors";
import {
  reset,
  setActiveErrors,
  setTouchedSnapshot,
  setErrorMode,
  setResolved,
} from "../slices/campaignValidationSlice";
import { validationSchemas } from "utils";
import { getAllErrorsLeafs } from "features/Library/Common/utils/validationUtils";
import { get, set } from "lodash";
import { type CampaignValidationState, type ValidationStepType } from "../../types/state";
import { type AnyObject } from "yup";
import {
  type FeedbackPageFormFields,
  type EmailTemplateFormFields,
  type LandingPageFormFields,
} from "../../Details/types";
import { publishCampaign } from "./campaignEntityStateThunk";

const turnErrorMode = (validationStep: ValidationStepType, on: boolean) => (dispatch: AppDispatch) => {
  dispatch(setErrorMode(on));
  dispatch(setResolved({ validationStep, isResolved: !on }));
};

const validateForm = async (
  validationStep: ValidationStepType,
  formFields: EmailTemplateFormFields | LandingPageFormFields | FeedbackPageFormFields,
  validationSchema: AnyObject,
  campaignValidation: CampaignValidationState,
  dispatch: AppDispatch,
) => {
  if (campaignValidation[validationStep].isFormValid) {
    return true;
  }

  if (campaignValidation[validationStep].initialized && !campaignValidation[validationStep].isFormValid) {
    dispatch(turnErrorMode(validationStep, true));
    return false;
  }

  try {
    await validationSchema.validate(formFields, { abortEarly: true });
    return true;
  } catch {
    dispatch(turnErrorMode(validationStep, true));
    return false;
  }
};

export const tryPublish = (id: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const emailTemplateFormFields = emailTemplateFormFieldsSelector(getState());
  const landingPageFormFields = landingPageFormFieldsSelector(getState());
  const feedbackPageFormFields = feedbackPageFormFieldsSelector(getState());
  const campaignValidation = campaignValidationSelector(getState());

  const emailTemplateValidPromise = validateForm(
    "emailTemplateValidation",
    emailTemplateFormFields,
    validationSchemas.campaignEmailTemplate,
    campaignValidation,
    dispatch,
  );

  const landingPageValidPromise = validateForm(
    "landingPageValidation",
    landingPageFormFields,
    validationSchemas.landingPage,
    campaignValidation,
    dispatch,
  );

  const feedbackPageValidPromise = validateForm(
    "feedbackPageValidation",
    feedbackPageFormFields,
    validationSchemas.feedbackPage,
    campaignValidation,
    dispatch,
  );

  const [emailTemplateValid, landingPageValid, feedbackPageValid] = await Promise.all([
    emailTemplateValidPromise,
    landingPageValidPromise,
    feedbackPageValidPromise,
  ]);

  if (!emailTemplateValid || !landingPageValid || !feedbackPageValid) return;

  dispatch(publishCampaign(id));
  dispatch(reset());
};

export const setActiveErrorsAction =
  (validationStep: ValidationStepType, errors: object) => (dispatch: AppDispatch) => {
    let activeErrors: { [key: string]: string } = {};
    let touched: object = {};
    getAllErrorsLeafs(errors, (key, value) => {
      activeErrors[key] = value;
      set(touched, key, true);
    });

    dispatch(setActiveErrors({ validationStep, activeErrors }));
    dispatch(setTouchedSnapshot({ validationStep, touched }));
  };

export const checkForResolvedErrors =
  (validationStep: ValidationStepType, errors: object) => (dispatch: AppDispatch, getState: () => RootState) => {
    const campaignValidation = campaignValidationSelector(getState());

    for (let item in campaignValidation[validationStep].activeErrors) {
      if (get(errors, item)) {
        return;
      }
    }

    dispatch(setResolved({ validationStep, isResolved: true }));
    dispatch(setTouchedSnapshot({ validationStep, touched: {} }));
    dispatch(setActiveErrors({ validationStep, activeErrors: {} }));
  };
